import { InputState, Rectangle } from '../../types/game.types';
import { BaseGame } from '../BaseGame';
import { RenderManager } from '../../core/RenderManager';
import { AudioManager } from '../../core/AudioManager';
import { Paddle } from './entities/Paddle';
import { Ball } from './entities/Ball';
import { Brick } from './entities/Brick';

export class BreakoutGame extends BaseGame {
  private paddle: Paddle | null = null;
  private ball: Ball | null = null;
  private bricks: Brick[] = [];
  
  // Game state
  private gameStarted: boolean = false;
  private ballLaunched: boolean = false;
  private ballsRemaining: number = 5; // Original Atari had 5 balls
  private wallsDestroyed: number = 0;
  private consecutiveHits: number = 0; // Track consecutive hits for speed
  private gameMode: number = 1; // Current game mode (1-12)
  private isTimedGame: boolean = false;
  private gameTimer: number = 0;
  private ballStuck: boolean = false; // For catch feature
  
  // Game constants
  private readonly PADDLE_WIDTH = 60;
  private readonly PADDLE_HEIGHT = 10;
  private readonly BALL_SIZE = 8;
  private readonly BRICK_ROWS = 6; // Original had 6 rows
  private readonly BRICK_COLS = 14;
  private readonly BRICK_WIDTH = 50;
  private readonly BRICK_HEIGHT = 15;
  private readonly BRICK_SPACING = 5;
  private readonly MAX_SCORE = 864; // Maximum possible score

  constructor(renderManager: RenderManager, audioManager: AudioManager) {
    const gameConfig = { width: 900, height: 600, fps: 60, scale: 1 }; // Landscape for mobile
    super(renderManager, audioManager, gameConfig, 'breakout');
  }

  public init(): void {
    console.log('Breakout Game initialized');
    
    // Reset game state
    this.score = 0;
    this.lives = 1; // Single game, not lives-based
    this.level = 1;
    this.isGameOver = false;
    this.gameStarted = true;
    this.ballLaunched = false;
    this.ballsRemaining = 5;
    this.wallsDestroyed = 0;
    this.consecutiveHits = 0;
    this.gameMode = 1; // Default to game 1
    this.gameTimer = 0;
    this.ballStuck = false;
    
    // Create game entities
    this.createPaddle();
    this.createBall();
    this.createBricks();
  }

  private createPaddle(): void {
    const paddleX = (this.config.width - this.PADDLE_WIDTH) / 2;
    const paddleY = this.config.height - 25;
    
    this.paddle = new Paddle(
      'paddle',
      paddleX,
      paddleY,
      this.PADDLE_WIDTH,
      this.PADDLE_HEIGHT,
      this.renderManager,
      this.audioManager
    );
  }

  private createBall(): void {
    const ballX = this.config.width / 2 - this.BALL_SIZE / 2;
    const ballY = this.config.height - 45; // Start on paddle
    
    this.ball = new Ball(
      'ball',
      ballX,
      ballY,
      this.BALL_SIZE,
      this.renderManager,
      this.audioManager
    );
  }

  private createBricks(): void {
    this.bricks = [];
    
    const totalBrickWidth = this.BRICK_COLS * this.BRICK_WIDTH + (this.BRICK_COLS - 1) * this.BRICK_SPACING;
    const startX = (this.config.width - totalBrickWidth) / 2;
    const startY = 80;
    
    // Original Atari color scheme (6 rows)
    const rowColors = [
      '#FF0000', // Red (7 points)
      '#FF6600', // Orange (7 points)
      '#FFFF00', // Yellow (4 points) 
      '#00FF00', // Green (4 points)
      '#00FFFF', // Aqua (1 point)
      '#0066FF'  // Blue (1 point)
    ];
    
    // Original Atari point values
    const rowPoints = [7, 7, 4, 4, 1, 1];
    
    for (let row = 0; row < this.BRICK_ROWS; row++) {
      for (let col = 0; col < this.BRICK_COLS; col++) {
        const x = startX + col * (this.BRICK_WIDTH + this.BRICK_SPACING);
        const y = startY + row * (this.BRICK_HEIGHT + this.BRICK_SPACING);
        
        const brick = new Brick(
          `brick_${row}_${col}`,
          x,
          y,
          this.BRICK_WIDTH,
          this.BRICK_HEIGHT,
          rowColors[row],
          this.renderManager,
          this.audioManager
        );
        
        // Set authentic Atari point values
        brick.setPointValue(rowPoints[row]);
        
        this.bricks.push(brick);
      }
    }
  }

  public update(deltaTime: number): void {
    if (this.isGameOver || !this.gameStarted) return;

    // Update timer for timed games
    if (this.isTimedGame && this.ballLaunched) {
      this.gameTimer += deltaTime;
    }

    // Update paddle
    if (this.paddle) {
      this.paddle.update(deltaTime);
    }

    // Update bricks (for animations)
    this.bricks.forEach(brick => {
      brick.update(deltaTime);
    });
    
    // Remove inactive bricks after their destroy animation completes
    this.bricks = this.bricks.filter(brick => brick.active);

    // Update ball
    if (this.ball && this.ballLaunched) {
      this.ball.update(deltaTime);
      
      // Clamp ball velocity to prevent runaway speeds
      this.clampBallVelocity();
      
      // Check ball bounds
      this.checkBallBounds();
      
      // Check collisions
      this.checkCollisions();
    } else if (this.ball && this.paddle && !this.ballLaunched) {
      // Ball follows paddle when not launched
      this.ball.setPosition(
        this.paddle.getPosition().x + this.PADDLE_WIDTH / 2 - this.BALL_SIZE / 2,
        this.paddle.getPosition().y - this.BALL_SIZE - 2
      );
    }

    // Check win condition
    this.checkWinCondition();
  }

  private checkBallBounds(): void {
    if (!this.ball) return;
    
    const ballPos = this.ball.getPosition();
    const ballVel = this.ball.getVelocity();
    
    // Side walls - keep ball in bounds and prevent tunneling
    if (ballPos.x <= 0) {
      this.ball.setVelocity(Math.abs(ballVel.x), ballVel.y);
      this.ball.setPosition(1, ballPos.y);
      this.audioManager.playTone(300, 0.1, 'square');
    } else if (ballPos.x >= this.config.width - this.BALL_SIZE) {
      this.ball.setVelocity(-Math.abs(ballVel.x), ballVel.y);
      this.ball.setPosition(this.config.width - this.BALL_SIZE - 1, ballPos.y);
      this.audioManager.playTone(300, 0.1, 'square');
    }
    
    // Top wall
    if (ballPos.y <= 0) {
      this.ball.setVelocity(ballVel.x, Math.abs(ballVel.y));
      this.ball.setPosition(ballPos.x, 1);
      this.audioManager.playTone(300, 0.1, 'square');
    }
    
    // Bottom - ball lost
    if (ballPos.y >= this.config.height) {
      this.ballLost();
    }
  }

  private checkCollisions(): void {
    if (!this.ball || !this.paddle) return;
    
    const ballBounds = this.ball.getBounds();
    const ballVel = this.ball.getVelocity();
    
    // Advanced paddle collision with 5 sections
    const paddleBounds = this.paddle.getBounds();
    if (this.rectangleIntersect(ballBounds, paddleBounds)) {
      const ballCenter = ballBounds.x + ballBounds.width / 2;
      const paddleLeft = paddleBounds.x;
      const paddleWidth = paddleBounds.width;
      const sectionWidth = paddleWidth / 5;
      
      // Determine which section was hit (0-4)
      const hitSection = Math.floor((ballCenter - paddleLeft) / sectionWidth);
      const clampedSection = Math.max(0, Math.min(4, hitSection));
      
      // Original Atari angles for each section
      const angles = [-60, -30, 0, 30, 60]; // Degrees
      const angleInRadians = (angles[clampedSection] * Math.PI) / 180;
      
      // Ball speed (will be modified by speed mechanics)
      let ballSpeed = 250;
      const currentSpeed = Math.sqrt(ballVel.x * ballVel.x + ballVel.y * ballVel.y);
      if (currentSpeed > 250) {
        ballSpeed = currentSpeed; // Maintain higher speed if already fast
      }
      
      // Calculate new velocity based on angle
      const newVelX = Math.sin(angleInRadians) * ballSpeed;
      const newVelY = -Math.abs(Math.cos(angleInRadians) * ballSpeed); // Always up
      
      this.ball.setVelocity(newVelX, newVelY);
      this.consecutiveHits++; // Count paddle hits
      
      // Center section causes ball to "jump" (straight up)
      if (clampedSection === 2) {
        this.ball.setVelocity(0, -ballSpeed); // Straight up
      }
      
      this.audioManager.playTone(400, 0.1, 'square');
    }
    
    // Brick collisions - only check one collision per frame to prevent rapid bouncing
    let hitBrick = false;
    for (let i = 0; i < this.bricks.length && !hitBrick; i++) {
      const brick = this.bricks[i];
      // Skip inactive bricks and bricks that are already being destroyed
      if (!brick.active || brick.isBeingDestroyed()) continue;
      
      const brickBounds = brick.getBounds();
      if (this.rectangleIntersect(ballBounds, brickBounds)) {
        hitBrick = true;
        
        // Calculate collision side more accurately
        const ballCenterX = ballBounds.x + ballBounds.width / 2;
        const ballCenterY = ballBounds.y + ballBounds.height / 2;
        const brickCenterX = brickBounds.x + brickBounds.width / 2;
        const brickCenterY = brickBounds.y + brickBounds.height / 2;
        
        // Calculate overlap on each axis
        const overlapX = Math.min(ballBounds.x + ballBounds.width - brickBounds.x, 
                                 brickBounds.x + brickBounds.width - ballBounds.x);
        const overlapY = Math.min(ballBounds.y + ballBounds.height - brickBounds.y,
                                 brickBounds.y + brickBounds.height - ballBounds.y);
        
        // Bounce based on smallest overlap (most likely collision direction)
        if (overlapX < overlapY) {
          // Horizontal collision (left/right side of brick)
          this.ball!.setVelocity(-ballVel.x, ballVel.y);
        } else {
          // Vertical collision (top/bottom of brick)
          this.ball!.setVelocity(ballVel.x, -ballVel.y);
        }
        
        // Move ball slightly away from brick to prevent sticking
        const ballPos = this.ball!.getPosition();
        if (overlapX < overlapY) {
          // Move horizontally
          if (ballCenterX < brickCenterX) {
            this.ball!.setPosition(brickBounds.x - this.BALL_SIZE - 1, ballPos.y);
          } else {
            this.ball!.setPosition(brickBounds.x + brickBounds.width + 1, ballPos.y);
          }
        } else {
          // Move vertically
          if (ballCenterY < brickCenterY) {
            this.ball!.setPosition(ballPos.x, brickBounds.y - this.BALL_SIZE - 1);
          } else {
            this.ball!.setPosition(ballPos.x, brickBounds.y + brickBounds.height + 1);
          }
        }
        
        // Destroy brick and award points
        brick.destroy();
        this.addScore(brick.getPointValue());
        this.audioManager.playExplosion();
        
        // Track consecutive hits for speed mechanics
        this.consecutiveHits++;
        
        // Speed up ball after 12 consecutive hits
        if (this.consecutiveHits >= 12) {
          this.increaseBallSpeed();
          this.consecutiveHits = 0; // Reset after speed increase
        }
        
        // Speed up ball if hitting top 3 rows (speed bricks)
        const brickRow = Math.floor((brick.getPosition().y - 80) / (this.BRICK_HEIGHT + this.BRICK_SPACING));
        if (brickRow < 3) {
          this.increaseBallSpeed();
        }
      }
    }
  }

  private rectangleIntersect(rect1: Rectangle, rect2: Rectangle): boolean {
    return !(rect1.x + rect1.width < rect2.x || 
             rect2.x + rect2.width < rect1.x ||
             rect1.y + rect1.height < rect2.y || 
             rect2.y + rect2.height < rect1.y);
  }

  private ballLost(): void {
    this.ballsRemaining--;
    this.ballLaunched = false;
    this.consecutiveHits = 0; // Reset hit counter
    this.ballStuck = false; // Reset catch state
    
    if (this.ballsRemaining <= 0) {
      this.gameOver();
    } else {
      // Reset ball position
      this.createBall();
    }
  }

  private checkWinCondition(): void {
    const activeBricks = this.bricks.filter(brick => brick.active);
    if (activeBricks.length === 0) {
      this.wallsDestroyed++;
      
      if (this.wallsDestroyed >= 2) {
        // Two walls destroyed - maximum score achieved!
        this.score = this.MAX_SCORE;
        this.gameOver();
      } else {
        // First wall destroyed - create second wall
        console.log('First wall destroyed! Creating second wall...');
        this.createBricks();
        this.ballLaunched = false;
        this.consecutiveHits = 0;
        this.createBall();
      }
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen
    this.renderManager.clear('#000000');
    
    // Render bricks (including those with destroy animations)
    this.bricks.forEach(brick => {
      brick.render(ctx);
    });
    
    // Render paddle
    if (this.paddle) {
      this.paddle.render(ctx);
    }
    
    // Render ball
    if (this.ball) {
      this.ball.render(ctx);
    }
    
    // Render HUD
    this.renderHUD();
    
    // Render game over screen
    if (this.isGameOver) {
      this.renderGameOver();
    }
    
    // Render initial entry if showing
    if (this.showingInitialEntry && this.initialEntry) {
      this.initialEntry.render(ctx, this.config.width / 2, this.config.height / 2);
    }
  }

  protected renderHUD(): void {
    // Scoreboard (upper left)
    if (this.isTimedGame) {
      const minutes = Math.floor(this.gameTimer / 60);
      const seconds = Math.floor(this.gameTimer % 60);
      this.renderManager.drawText(
        `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
        20,
        20,
        '#FFFFFF',
        '18px "Courier New", monospace'
      );
    } else {
      this.renderManager.drawText(
        this.score.toString().padStart(3, '0'),
        20,
        20,
        '#FFFFFF',
        '18px "Courier New", monospace'
      );
    }
    
    // Game Number (upper center)
    this.renderManager.drawTextCentered(
      `${this.gameMode}`,
      this.config.width / 2,
      20,
      '#FFFFFF',
      '18px "Courier New", monospace'
    );
    
    // Number of Players/Team (upper right) - always 1 for single player
    this.renderManager.drawText(
      '1',
      this.config.width - 50,
      20,
      '#FFFFFF',
      '18px "Courier New", monospace'
    );
    
    // Balls remaining (upper right, second line)
    this.renderManager.drawText(
      this.ballsRemaining.toString(),
      this.config.width - 50,
      45,
      '#FFFFFF',
      '16px "Courier New", monospace'
    );
    
    // Walls destroyed indicator
    this.renderManager.drawText(
      `WALLS: ${this.wallsDestroyed}/2`,
      20,
      50,
      '#CCCCCC',
      '12px "Courier New", monospace'
    );
    
    // Max possible score reminder
    this.renderManager.drawText(
      `MAX: ${this.MAX_SCORE}`,
      20,
      70,
      '#CCCCCC',
      '12px "Courier New", monospace'
    );
    
    // Launch instruction
    if (!this.ballLaunched) {
      this.renderManager.drawTextCentered(
        'PRESS SPACE TO SERVE BALL',
        this.config.width / 2,
        this.config.height - 20,
        '#FFFF00',
        '14px "Courier New", monospace'
      );
    }
    
    // Controls
    this.renderManager.drawText(
      'CONTROLS: A/D = Move Paddle, Space = Serve/Catch',
      20,
      this.config.height - 40,
      '#CCCCCC',
      '12px "Courier New", monospace'
    );
  }

  protected renderGameOver(): void {
    const centerX = this.config.width / 2;
    const centerY = this.config.height / 2;
    
    this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
    
    this.renderManager.drawTextCentered(
      'GAME OVER',
      centerX,
      centerY - 60,
      '#FFFFFF',
      '32px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `FINAL SCORE: ${this.score}`,
      centerX,
      centerY - 20,
      '#FFFFFF',
      '18px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `REACHED LEVEL ${this.level}`,
      centerX,
      centerY + 10,
      '#CCCCCC',
      '16px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      'PRESS SPACE TO RESTART',
      centerX,
      centerY + 60,
      '#FFFFFF',
      '14px "Courier New", monospace'
    );
  }

  public handleInput(input: InputState): void {
    if (this.isGameOver) {
      if (this.showingInitialEntry) {
        return;
      }
      
      if (input.keys['Space'] || input.touch.gesture === 'tap') {
        this.restart();
      }
      return;
    }

    if (!this.paddle) return;

    // Touch input for paddle movement (direct position control)
    if (input.touch.active) {
      const touchX = input.touch.position.x;
      const paddleX = this.paddle.getPosition().x;
      const paddleCenter = paddleX + this.PADDLE_WIDTH / 2;
      const diff = touchX - paddleCenter;
      
      // Set movement direction based on touch position relative to paddle
      if (Math.abs(diff) > 10) {
        this.paddle.setMovement(diff > 0 ? 1 : -1);
      } else {
        this.paddle.setMovement(0);
      }
      
      // Launch ball on tap
      if (input.touch.gesture === 'tap' && !this.ballLaunched) {
        this.launchBall();
      }
    } else {
      // Keyboard controls
      let moveX = 0;
      
      if (input.keys['ArrowLeft'] || input.keys['KeyA']) {
        moveX = -1;
      }
      if (input.keys['ArrowRight'] || input.keys['KeyD']) {
        moveX = 1;
      }
      
      this.paddle.setMovement(moveX);
      
      // Launch ball
      if (input.keys['Space'] && !this.ballLaunched) {
        this.launchBall();
      }
    }
  }

  private launchBall(): void {
    if (!this.ball) return;
    
    // Launch ball at slight angle
    const angle = (Math.random() - 0.5) * 0.5; // Small random angle
    const speed = 250;
    
    this.ball.setVelocity(
      Math.sin(angle) * speed,
      -Math.cos(angle) * speed
    );
    
    this.ballLaunched = true;
    this.audioManager.playShoot();
  }

  private increaseBallSpeed(): void {
    if (!this.ball) return;
    
    const currentVel = this.ball.getVelocity();
    const currentSpeed = Math.sqrt(currentVel.x * currentVel.x + currentVel.y * currentVel.y);
    const maxSpeed = 400; // Maximum ball speed
    
    if (currentSpeed < maxSpeed) {
      const speedMultiplier = 1.1; // 10% speed increase (reduced from 15%)
      const newVelX = currentVel.x * speedMultiplier;
      const newVelY = currentVel.y * speedMultiplier;
      
      // Ensure we don't exceed max speed
      const newSpeed = Math.sqrt(newVelX * newVelX + newVelY * newVelY);
      if (newSpeed <= maxSpeed) {
        this.ball.setVelocity(newVelX, newVelY);
        console.log('Ball speed increased!');
      }
    }
  }
  
  private clampBallVelocity(): void {
    if (!this.ball) return;
    
    const vel = this.ball.getVelocity();
    const speed = Math.sqrt(vel.x * vel.x + vel.y * vel.y);
    const maxSpeed = 400;
    const minSpeed = 150;
    
    if (speed > maxSpeed) {
      // Scale down to max speed
      const scale = maxSpeed / speed;
      this.ball.setVelocity(vel.x * scale, vel.y * scale);
    } else if (speed < minSpeed && speed > 0) {
      // Scale up to min speed
      const scale = minSpeed / speed;
      this.ball.setVelocity(vel.x * scale, vel.y * scale);
    }
    
    // Ensure minimum vertical component to prevent horizontal-only movement
    const newVel = this.ball.getVelocity();
    if (Math.abs(newVel.y) < 50) {
      const sign = newVel.y >= 0 ? 1 : -1;
      this.ball.setVelocity(newVel.x, sign * 50);
    }
  }
  
  public cleanup(): void {
    super.cleanup();
    this.bricks = [];
    this.paddle = null;
    this.ball = null;
  }
}